import React, { useEffect, useState } from "react";
import axios from "axios";
import { API } from "../../../App";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../ui/card";
import { Button } from "../../ui/button";
import { toast } from "sonner";
import { FileText, Plus, Star, Copy, Trash2, Pencil, Loader2, StarOff } from "lucide-react";
import TechReportTemplateEditor from "./TechReportTemplateEditor";

const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } });

const TechReportTemplatesTab = () => {
  const [templates, setTemplates] = useState([]);
  const [registry, setRegistry] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);

  const load = async () => {
    try {
      const [t, r] = await Promise.all([
        axios.get(`${API}/report-templates`, auth()),
        axios.get(`${API}/report-templates/registry`, auth()),
      ]);
      setTemplates(t.data.templates || []);
      setRegistry(r.data);
    } catch (e) {
      toast.error(e.response?.status === 403
        ? "Only owners/admins can manage report templates"
        : "Failed to load report templates");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const createTemplate = async () => {
    setCreating(true);
    try {
      const res = await axios.post(`${API}/report-templates`, { name: "Custom Technician Report" }, auth());
      setTemplates((p) => [...p, res.data]);
      setEditing(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create template");
    } finally {
      setCreating(false);
    }
  };

  const duplicate = async (t) => {
    try {
      const res = await axios.post(`${API}/report-templates/${t.template_id}/duplicate`, {}, auth());
      setTemplates((p) => [...p, res.data]);
      toast.success("Template duplicated");
    } catch { toast.error("Failed to duplicate"); }
  };

  const remove = async (t) => {
    if (!window.confirm(`Delete template "${t.name}"? Reports will fall back to the built-in standard PDF.`)) return;
    try {
      await axios.delete(`${API}/report-templates/${t.template_id}`, auth());
      setTemplates((p) => p.filter((x) => x.template_id !== t.template_id));
      toast.success("Template deleted");
    } catch { toast.error("Failed to delete"); }
  };

  const setDefault = async (t, on) => {
    try {
      await axios.post(`${API}/report-templates/${t.template_id}/${on ? "set-default" : "clear-default"}`, {}, auth());
      setTemplates((p) => p.map((x) => ({ ...x, is_default: on ? x.template_id === t.template_id : (x.template_id === t.template_id ? false : x.is_default) })));
      toast.success(on ? `"${t.name}" is now the default technician report` : "Default cleared — using the built-in standard PDF");
    } catch { toast.error("Failed to update default"); }
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>;

  return (
    <Card className="glass-card border-theme" data-testid="tech-report-templates-card">
      <CardHeader>
        <CardTitle className="text-foreground text-base font-['Manrope'] flex items-center gap-2">
          <FileText className="w-4 h-4 text-[#FFCC00]" /> Technician Report PDF Templates
        </CardTitle>
        <CardDescription>
          Customize exactly what appears on the Technician Report PDF and in what order — sections, job columns,
          labels, branding, signatures and layout. Templates change presentation only: commission, tax, payment and
          settlement calculations are never affected.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {templates.length === 0 ? (
          <div className="text-center py-8 border border-dashed border-theme rounded-lg">
            <p className="text-muted-foreground text-sm mb-3">No custom templates yet — reports use the built-in Fieldz AI standard layout.</p>
            <Button onClick={createTemplate} disabled={creating} className="bg-[#FFCC00] text-black hover:bg-[#FFCC00]/80" data-testid="customize-pdf-report-btn">
              {creating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Pencil className="w-4 h-4 mr-2" />} Customize PDF Report
            </Button>
          </div>
        ) : (
          <>
            {templates.map((t) => (
              <div key={t.template_id} className="flex items-center gap-3 rounded-lg border border-theme bg-theme-surface px-3 py-2.5" data-testid={`template-row-${t.template_id}`}>
                <FileText className="w-4 h-4 text-muted-foreground shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-foreground truncate">
                    {t.name}
                    {t.is_default && <span className="ml-2 inline-flex items-center gap-1 text-[10px] font-semibold text-[#FFCC00] uppercase"><Star className="w-3 h-3 fill-current" /> Default</span>}
                  </p>
                  <p className="text-xs text-muted-foreground">v{t.version} · updated {t.updated_at ? new Date(t.updated_at).toLocaleDateString() : "—"}</p>
                </div>
                <Button variant="outline" size="sm" className="h-8" onClick={() => setEditing(t)} data-testid={`edit-template-${t.template_id}`}>
                  <Pencil className="w-3.5 h-3.5 mr-1.5" /> Edit
                </Button>
                <Button variant="ghost" size="icon" className="h-8 w-8" title={t.is_default ? "Clear default (use built-in standard)" : "Set as default"}
                  onClick={() => setDefault(t, !t.is_default)} data-testid={`default-template-${t.template_id}`}>
                  {t.is_default ? <StarOff className="w-4 h-4" /> : <Star className="w-4 h-4" />}
                </Button>
                <Button variant="ghost" size="icon" className="h-8 w-8" title="Duplicate" onClick={() => duplicate(t)} data-testid={`duplicate-template-${t.template_id}`}>
                  <Copy className="w-4 h-4" />
                </Button>
                <Button variant="ghost" size="icon" className="h-8 w-8 text-red-500" title="Delete" onClick={() => remove(t)} data-testid={`delete-template-${t.template_id}`}>
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            ))}
            <Button variant="outline" size="sm" onClick={createTemplate} disabled={creating} data-testid="new-template-btn">
              {creating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />} New Template
            </Button>
          </>
        )}
        <p className="text-xs text-muted-foreground">
          When a default is set, Export PDF / Email PDF on the Technicians report use it automatically — a picker on
          that tab lets you switch per export. Every generated report stores a version snapshot, so historical
          settlement PDFs keep their original layout even after you change a template.
        </p>
      </CardContent>

      {editing && registry && (
        <TechReportTemplateEditor
          template={editing}
          registry={registry}
          onClose={() => setEditing(null)}
          onSaved={(updated) => {
            setTemplates((p) => p.map((x) => (x.template_id === updated.template_id ? { ...x, ...updated } : x)));
            setEditing(null);
          }}
        />
      )}
    </Card>
  );
};

export default TechReportTemplatesTab;
